/**
 * TemplateService.ts;
 * Service unifié pour la gestion des templates;
 * Fusionne TemplateManager et TemplateGallery;
 * CORRIGÉ: Ajout du chargement depuis l'API backend avec fallback local
 */
import {
  TemplateFormat,
  TemplateUnit,
  // TemplateOrientation,
} from '../types';
import { logger } from '../utils/ProfessionalLogger';
import { APP_CONFIG } from '../config/app.config';
import { supabase, getSupabaseClient } from './SupabaseService';
import { SUPABASE_CONFIG } from '../config/supabase.config';
// PaginationService supprimé - implémentation simple inline
interface PaginatedResult<T> {
  items: T[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  hasNext: boolean;
  hasPrevious: boolean;
}
export interface TemplatePreset {
  id: string;
  name: string;
  description: string;
  category: string;
  thumbnail: string;
  template: string; // JSON du template;
  tags: string[];
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  dimensions: { width: number; height: number };
}
export class TemplateService {
  private currentTemplate: TemplateFormat;
  private presets: TemplatePreset[] = [];
  // Pagination simple inline (remplace PaginationService supprimé)
  private pageSize: number = 12;
  private currentPage: number = 1;
  private onTemplateChange: ((template: TemplateFormat) => void) | null = null;
  private onTemplateSelectCallback?: (template: TemplatePreset) => void;
  private storageMode: 'backend' | 'offline' | 'auto';
  private templatesLoaded: boolean = false;
  private loadingPromise: Promise<void> | null = null;

  constructor() {
    this.storageMode = (APP_CONFIG.STORAGE_MODE as 'backend' | 'offline' | 'auto') || 'auto';

    // Template par défaut;
    this.currentTemplate = {
      name: 'Personnalisé',
      width: 800,
      height: 600,
      unit: 'px',
      orientation: 'landscape',
      category: 'custom',
    };

    // Charger les templates (depuis API ou local)
    this.loadTemplates();
  }

  /**
   * Charge les templates depuis l'API backend ou depuis les presets locaux
   * CORRIGÉ: Intégration API backend avec fallback local
   */
  private async loadTemplates(): Promise<void> {
    // Éviter les chargements multiples simultanés
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = (async () => {
      try {
        // D'abord, initialiser les presets locaux (fallback)
        this.initializePresets();

        // Ensuite, essayer de charger depuis l'API si mode backend ou auto
        if (this.storageMode === 'backend' || this.storageMode === 'auto') {
          try {
            const apiTemplates = await this.fetchTemplatesFromAPI();
            if (apiTemplates && apiTemplates.length > 0) {
              // Fusionner les templates API avec les templates locaux
              // Les templates API ont priorité sur les templates locaux
              const localTemplateIds = new Set(this.presets.map(t => t.id));
              const _newApiTemplates = apiTemplates.filter(t => !localTemplateIds.has(t.id));
              this.presets = [...apiTemplates, ...this.presets];
              logger.info(`✅ ${apiTemplates.length} templates chargés depuis l'API backend`);
            }
          } catch (error) {
            logger.warn(
              "⚠️ Impossible de charger les templates depuis l'API, utilisation des templates locaux:",
              error
            );
            // En mode 'auto', on continue avec les templates locaux
            if (this.storageMode === 'backend') {
              throw error; // En mode 'backend' strict, propager l'erreur
            }
          }
        }

        this.templatesLoaded = true;
      } catch (error) {
        logger.error('❌ Erreur lors du chargement des templates:', error);
        // En cas d'erreur, on garde les templates locaux initialisés
        this.templatesLoaded = true;
      } finally {
        this.loadingPromise = null;
      }
    })();

    return this.loadingPromise;
  }

  /**
   * Récupère les templates depuis Supabase ou l'API backend
   * CORRIGÉ: Charge depuis Supabase si configuré, sinon fallback sur API backend
   */
  private async fetchTemplatesFromAPI(): Promise<TemplatePreset[]> {
    // Supabase est la seule source de vérité (P2.1). Le fallback Express a été retiré.
    if (!SUPABASE_CONFIG.url || !SUPABASE_CONFIG.anonKey || !supabase) {
      logger.warn(
        'Supabase non configuré — aucun template distant chargé, presets locaux uniquement.'
      );
      return [];
    }
    try {
      const { data, error } = await supabase
        .from('template_editor_templates')
        .select(
          'id, name, template_data, thumbnail_url, created_at, description, category, tags, difficulty, dimensions'
        )
        .eq('is_public', true)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        logger.warn('Erreur Supabase template_editor_templates:', error);
        return [];
      }

      // Convertir le format Supabase en TemplatePreset
      interface SupabaseTemplateRow {
        id: string;
        name: string;
        description?: string;
        category?: string;
        thumbnail_url?: string;
        template_data?: Record<string, unknown>;
        dimensions?: { width?: number; height?: number } | null;
        tags?: string[];
        difficulty?: 'beginner' | 'intermediate' | 'advanced';
        [key: string]: unknown;
      }
      // Forme attendue dans `template_data` (JSONB) :
      //   { canvas?: { templateWidth, templateHeight }, width?, height? }
      interface TemplateDataPayload {
        canvas?: { templateWidth?: number; templateHeight?: number };
        width?: number;
        height?: number;
      }
      // P3 : data est typé Json (union large) ; cast en SupabaseTemplateRow[].
      return ((data ?? []) as unknown as SupabaseTemplateRow[]).map(template => {
        const templateData = (template.template_data || {}) as TemplateDataPayload;
        const dimensions = template.dimensions as { width?: number; height?: number } | null;
        return {
          id: template.id,
          name: template.name,
          description: template.description || 'Template public',
          category: template.category || 'public',
          thumbnail: template.thumbnail_url || '',
          template: JSON.stringify(templateData),
          tags: Array.isArray(template.tags) ? template.tags : [],
          difficulty: (template.difficulty || 'beginner') as
            | 'beginner'
            | 'intermediate'
            | 'advanced',
          dimensions:
            dimensions && dimensions.width && dimensions.height
              ? {
                  width: dimensions.width,
                  height: dimensions.height,
                }
              : {
                  width: templateData.canvas?.templateWidth || templateData.width || 800,
                  height: templateData.canvas?.templateHeight || templateData.height || 600,
                },
        };
      });
    } catch (error) {
      logger.warn('Erreur Supabase templates, presets locaux uniquement:', error);
      return [];
    }
  }

  // normalizeTemplatePreset retiré en P2.1 (plus de fallback Express).

  /**
   * Initialise les templates prédéfinis locaux (fallback);
   */
  private initializePresets(): void {
    // Charger seulement les templates initiaux (première page)
    const initialPresets: TemplatePreset[] = [
      {
        id: 'wedding-classic',
        name: 'Mariage Classique',
        description: 'Template élégant pour mariage avec espace photo et texte personnalisé',
        category: 'wedding',
        thumbnail: '/templates/wedding-classic-thumb.jpg',
        template: JSON.stringify({
          width: 800,
          height: 600,
          elements: [
            {
              type: 'text',
              content: 'Mariage de [NOM] & [NOM]',
              x: 400,
              y: 100,
              fontSize: 32,
              fontFamily: 'serif',
              color: '#8B4513',
            },
            {
              type: 'image',
              src: '/placeholders/photo-placeholder.jpg',
              x: 300,
              y: 200,
              width: 200,
              height: 200,
            },
          ],
        }),
        tags: ['mariage', 'classique', 'élégant'],
        difficulty: 'beginner',
        dimensions: { width: 800, height: 600 },
      },
      {
        id: 'birthday-modern',
        name: 'Anniversaire Moderne',
        description: 'Design moderne et coloré pour anniversaire',
        category: 'birthday',
        thumbnail: '/templates/birthday-modern-thumb.jpg',
        template: JSON.stringify({
          width: 600,
          height: 600,
          elements: [
            {
              type: 'text',
              content: 'Joyeux Anniversaire !',
              x: 300,
              y: 150,
              fontSize: 28,
              fontFamily: 'sans-serif',
              color: '#FF6B6B',
            },
          ],
        }),
        tags: ['anniversaire', 'moderne', 'coloré'],
        difficulty: 'beginner',
        dimensions: { width: 600, height: 600 },
      },
      {
        id: 'business-card',
        name: 'Carte de Visite',
        description: 'Template professionnel pour carte de visite',
        category: 'business',
        thumbnail: '/templates/business-card-thumb.jpg',
        template: JSON.stringify({
          width: 400,
          height: 250,
          elements: [
            {
              type: 'text',
              content: '[NOM]',
              x: 200,
              y: 80,
              fontSize: 24,
              fontFamily: 'sans-serif',
              color: '#333333',
            },
            {
              type: 'text',
              content: '[TITRE]',
              x: 200,
              y: 120,
              fontSize: 16,
              fontFamily: 'sans-serif',
              color: '#666666',
            },
          ],
        }),
        tags: ['professionnel', 'carte', 'business'],
        difficulty: 'intermediate',
        dimensions: { width: 400, height: 250 },
      },
    ];
    // Stocker tous les presets
    this.presets = initialPresets;
    this.currentPage = 1;
    logger.info(`📄 ${initialPresets.length} templates chargés`);
  }
  /**
   * Définit le callback pour les changements de template;
   */
  public setOnTemplateChangeCallback(callback: (template: TemplateFormat) => void): void {
    this.onTemplateChange = callback;
  }
  /**
   * Définit le callback pour la sélection de template;
   */
  public setOnTemplateSelect(callback: (template: TemplatePreset) => void): void {
    this.onTemplateSelectCallback = callback;
  }
  /**
   * Obtient le template actuel;
   */
  public getCurrentTemplate(): TemplateFormat {
    return { ...this.currentTemplate };
  }
  /**
   * Définit un nouveau template;
   */
  public async setTemplate(template: TemplateFormat): Promise<boolean> {
    try {
      // Implémentation via TemplateValidator;
      try {
        const { TemplateValidator } = await import('../utils/TemplateValidator');
        const templateValidator = new TemplateValidator();
        const validationResult = templateValidator.validateTemplate(template);
        if (!validationResult.isValid) {
          logger.error('❌ Template invalide:', validationResult.errors);
          return false;
        }
      } catch (error) {
        logger.warn('TemplateValidator non disponible, validation ignorée:', error);
      }

      this.currentTemplate = { ...template };
      if (this.onTemplateChange) {
        this.onTemplateChange(this.currentTemplate);
      }
      logger.info('✅ Template mis à jour:', this.currentTemplate);
      return true;
    } catch (error) {
      logger.error('Erreur lors de la validation du template:', error);
      return false;
    }
  }
  // `applyPreset` supprimée en P2.3 audit TS : la méthode mélangeait deux
  // structures `TemplatePreset` distinctes (la collection de formats
  // déclarée dans `types.ts` vs la carte de template locale), si bien
  // qu'elle retournait toujours false à l'exécution. Aucun appelant vivant.
  /**
   * Obtient tous les presets disponibles (sans pagination - pour compatibilité);
   * CORRIGÉ: Attendre le chargement si nécessaire
   */
  public async getPresets(): Promise<TemplatePreset[]> {
    if (!this.templatesLoaded && this.loadingPromise) {
      await this.loadingPromise;
    }
    return [...this.presets];
  }

  /**
   * Version synchrone pour compatibilité (retourne les templates déjà chargés)
   */
  public getPresetsSync(): TemplatePreset[] {
    return [...this.presets];
  }

  /**
   * Obtient les presets paginés (recommandé pour les performances);
   * CORRIGÉ: Version asynchrone pour attendre le chargement
   */
  public async getPresetsPaginated(page?: number): Promise<PaginatedResult<TemplatePreset>> {
    if (!this.templatesLoaded && this.loadingPromise) {
      await this.loadingPromise;
    }

    if (page !== undefined) {
      this.currentPage = page;
    }
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    const pageItems = this.presets.slice(startIndex, endIndex);
    const totalPages = Math.ceil(this.presets.length / this.pageSize);

    return {
      items: pageItems,
      currentPage: this.currentPage,
      totalPages,
      totalItems: this.presets.length,
      hasNext: this.currentPage < totalPages,
      hasPrevious: this.currentPage > 1,
    };
  }

  /**
   * Version synchrone pour compatibilité
   */
  public getPresetsPaginatedSync(page?: number): PaginatedResult<TemplatePreset> {
    if (page !== undefined) {
      this.currentPage = page;
    }
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    const pageItems = this.presets.slice(startIndex, endIndex);
    const totalPages = Math.ceil(this.presets.length / this.pageSize);

    return {
      items: pageItems,
      currentPage: this.currentPage,
      totalPages,
      totalItems: this.presets.length,
      hasNext: this.currentPage < totalPages,
      hasPrevious: this.currentPage > 1,
    };
  }

  /**
   * Recharge les templates depuis l'API (utile pour rafraîchir la liste)
   */
  public async refreshTemplates(): Promise<void> {
    this.templatesLoaded = false;
    this.presets = [];
    await this.loadTemplates();
  }

  /**
   * Charge plus de templates si nécessaire (lazy loading);
   */
  public async loadMoreTemplates(): Promise<PaginatedResult<TemplatePreset> | null> {
    const totalPages = Math.ceil(this.presets.length / this.pageSize);
    if (this.currentPage >= totalPages) {
      return null;
    }
    this.currentPage++;
    return this.getPresetsPaginated();
  }

  /**
   * Obtient les statistiques de pagination;
   */
  public getPaginationStats(): Promise<PaginatedResult<TemplatePreset>> {
    return this.getPresetsPaginated();
  }
  /**
   * Obtient les presets par catégorie;
   */
  public getPresetsByCategory(category: string): TemplatePreset[] {
    return this.presets.filter(preset => preset.category === category);
  }
  /**
   * Sélectionne un template preset;
   */
  public selectTemplate(template: TemplatePreset): void {
    if (this.onTemplateSelectCallback) {
      this.onTemplateSelectCallback(template);
    }
  }
  /**
   * Convertit les dimensions entre unités;
   */
  public async convertDimensions(
    width: number,
    height: number,
    fromUnit: TemplateUnit,
    toUnit: TemplateUnit
  ): Promise<{ width: number; height: number }> {
    try {
      // Implémentation via UnitConverter;
      const { UnitConverter: UnitConverterUtil } = await import('../utils/UnitConverter');
      type LengthUnit = import('../utils/UnitConverter').LengthUnit;
      const unitConverter = UnitConverterUtil.getInstance();
      // `TemplateUnit` accepte 'inch' que `LengthUnit` ne connaît pas : on aplatit.
      const toLengthUnit = (u: TemplateUnit): LengthUnit => (u === 'inch' ? 'in' : u) as LengthUnit;
      const fromUnitTyped = toLengthUnit(fromUnit);
      const toUnitTyped = toLengthUnit(toUnit);
      const convertedWidth = unitConverter.convertLength(width, fromUnitTyped, toUnitTyped);
      const convertedHeight = unitConverter.convertLength(height, fromUnitTyped, toUnitTyped);
      return {
        width: convertedWidth.value,
        height: convertedHeight.value,
      };
    } catch (error) {
      logger.error('Erreur lors de la conversion des dimensions:', error);
      // Fallback: utiliser la conversion basique si UnitConverter n'est pas disponible
      return { width, height };
    }
  }
  /**
   * Valide les dimensions d'un template;
   */
  public async validateDimensions(
    width: number,
    height: number,
    _unit: TemplateUnit
  ): Promise<boolean> {
    try {
      // Implémentation via DimensionValidator;
      const { DimensionValidator: DimValidator } = await import('../utils/DimensionValidator');
      const dimensionValidator = DimValidator.getInstance();
      const result = dimensionValidator.validateDimensions(width, height);
      return result.isValid;
    } catch (error) {
      logger.warn('DimensionValidator non disponible, validation basique appliquée:', error);
      // Validation basique de secours
      return width > 0 && height > 0 && width <= 10000 && height <= 10000;
    }
  }
  /**
   * Obtient les catégories disponibles;
   */
  public getCategories(): string[] {
    const categories = new Set(this.presets.map(preset => preset.category));
    return Array.from(categories);
  }
  /**
   * Recherche des templates par tags;
   */
  public searchByTags(tags: string[]): TemplatePreset[] {
    return this.presets.filter(preset => tags.some(tag => preset.tags.includes(tag.toLowerCase())));
  }
  /**
   * Obtient les templates par niveau de difficulté
   */
  public getByDifficulty(difficulty: 'beginner' | 'intermediate' | 'advanced'): TemplatePreset[] {
    return this.presets.filter(preset => preset.difficulty === difficulty);
  }

  /**
   * PR-08 : Publie un projet comme template public
   *
   * @param projectId - ID du projet à publier
   * @param meta - Métadonnées optionnelles (name, description)
   * @returns Promise<string> - ID du template créé
   */
  public async publishTemplate(
    projectId: string,
    meta?: { name?: string; description?: string }
  ): Promise<{ data: string | null; error: Error | null }> {
    const supabase = getSupabaseClient();
    if (!supabase) {
      return { data: null, error: new Error('Supabase non configuré') };
    }

    try {
      const { data, error } = await supabase.rpc('publish_template', {
        project_id: projectId,
        meta: meta || {},
      });

      if (error) {
        logger.error('[TemplateService] Erreur lors de la publication du template:', error);
        return { data: null, error: new Error(error.message) };
      }

      logger.info(`[TemplateService] Template publié avec succès: ${data}`);
      return { data, error: null };
    } catch (error) {
      logger.error('[TemplateService] Exception lors de la publication:', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * PR-08 : Duplique un template public dans les projets de l'utilisateur
   *
   * @param templateId - ID du template à dupliquer
   * @param newName - Nouveau nom pour le projet
   * @returns Promise<string> - ID du projet créé
   */
  public async duplicatePublicTemplate(
    templateId: string,
    newName: string
  ): Promise<{ data: string | null; error: Error | null }> {
    const supabase = getSupabaseClient();
    if (!supabase) {
      return { data: null, error: new Error('Supabase non configuré') };
    }

    try {
      // Récupérer le template
      const { data: template, error: fetchError } = await supabase
        .from('template_editor_templates')
        .select('template_data, name')
        .eq('id', templateId)
        .eq('is_public', true)
        .single();

      if (fetchError || !template) {
        return { data: null, error: new Error('Template non trouvé') };
      }

      // Récupérer l'utilisateur actuel
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();
      if (userError || !user) {
        return { data: null, error: new Error('Utilisateur non authentifié') };
      }

      // Créer un nouveau projet avec les données du template
      const { data: project, error: createError } = await supabase
        .from('template_editor_projects')
        .insert({
          user_id: user.id,
          name: newName,
          canvas_data: template.template_data,
          project_type: 'template_editor',
        })
        .select('id')
        .single();

      if (createError || !project) {
        return { data: null, error: new Error('Erreur lors de la création du projet') };
      }

      logger.info(`[TemplateService] Template dupliqué: ${templateId} -> ${project.id}`);
      return { data: project.id, error: null };
    } catch (error) {
      logger.error('[TemplateService] Exception lors de la duplication:', error);
      return { data: null, error: error as Error };
    }
  }
}
